import { QueryClient, useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getAllScoreKey } from "src/api/sure-api/score/useGetAllScore";
import { getAllScoreTypeKey, ScoreType } from "./useGetAllScoreType";

type ScoreTypesCache = { data: ScoreType[] };

type UpdateScoreTypeOptions = {
    onSuccess?: (scoreType: ScoreType, previousScoreType: ScoreType | undefined) => void;
    onError?: () => void;
};

const updateScoreTypeMutationKey = "updateScoreType" as const;

const latestSaveIds = new WeakMap<QueryClient, Map<ScoreType["type"], symbol>>();

const replaceScoreType = (cache: ScoreTypesCache, next: ScoreType): ScoreTypesCache => {
    const exists = cache.data.some((scoreType) => scoreType.type === next.type);

    return {
        data:
            exists ?
                cache.data.map((scoreType) => (scoreType.type === next.type ? next : scoreType))
            :   [...cache.data, next],
    };
};

export const useUpdateScoreType = (options: UpdateScoreTypeOptions = {}) => {
    const queryClient = useQueryClient();
    const latestSaveIdByType = latestSaveIds.get(queryClient) ?? new Map<ScoreType["type"], symbol>();
    latestSaveIds.set(queryClient, latestSaveIdByType);

    return useMutation({
        mutationKey: [updateScoreTypeMutationKey],
        mutationFn: async (scoreType: ScoreType) => {
            const {
                type,
                scoreIds,
                variationIds,
                defaultVariationIds,
                subscoreIds,
                variationOrder,
                actionPlanScoreIds,
            } = scoreType;

            return await loggedInApiCall.put(`/api/score_types/${type}`, {
                scoreIds: scoreIds,
                variationIds: variationIds,
                defaultVariationIds: defaultVariationIds,
                subscoreIds: subscoreIds,
                variationOrder: variationOrder,
                actionPlanScoreIds: actionPlanScoreIds,
            });
        },
        onMutate: async (scoreType: ScoreType) => {
            const saveId = Symbol();
            latestSaveIdByType.set(scoreType.type, saveId);
            await queryClient.cancelQueries([getAllScoreTypeKey]);
            const previous = queryClient.getQueryData<ScoreTypesCache>([getAllScoreTypeKey]);
            if (previous !== undefined) {
                queryClient.setQueryData<ScoreTypesCache>([getAllScoreTypeKey], replaceScoreType(previous, scoreType));
            }

            return { previous, saveId };
        },
        onSuccess: (_response, scoreType, context) => {
            const previousScoreType = context?.previous?.data.find((cached) => cached.type === scoreType.type);
            options.onSuccess?.(scoreType, previousScoreType);
        },
        onError: (_error, scoreType, context) => {
            const current = queryClient.getQueryData<ScoreTypesCache>([getAllScoreTypeKey]);
            if (
                context?.previous !== undefined &&
                current !== undefined &&
                latestSaveIdByType.get(scoreType.type) === context.saveId
            ) {
                const previousScoreType = context.previous.data.find((cached) => cached.type === scoreType.type);
                queryClient.setQueryData<ScoreTypesCache>(
                    [getAllScoreTypeKey],
                    previousScoreType !== undefined ?
                        replaceScoreType(current, previousScoreType)
                    :   { ...current, data: current.data.filter((cached) => cached.type !== scoreType.type) },
                );
            }
            options.onError?.();
        },
        onSettled: () => {
            if (queryClient.isMutating({ mutationKey: [updateScoreTypeMutationKey] }) > 1) return;

            queryClient.invalidateQueries([getAllScoreTypeKey]);
            queryClient.invalidateQueries([getAllScoreKey]);
        },
    });
};
